import { useEffect, useMemo, useRef, useState } from "react";
import { getPersona } from "../../definitions/modelPlaceholders";

export function useTypingPlaceholders(model, active) {
  const persona = getPersona(model);
  const { placeholders, speedMs } = persona;

  // Slowness knobs (tweak to taste)
  const PER_ITEM_MULT = 3;      // multiply every rotation delay
  const FIRST_LINGER_MULT = 5;     // extra linger on the very first item
  const CYCLE_PAUSE_MULT = 6;      // big pause when wrapping from last -> first
  const MIN_DELAY_MS = 1200;       // never go faster than this per rotation
  const JITTER_PCT = 0.05;         // small ±5% jitter so it doesn't feel robotic

  const [i, setI] = useState(0);
  const timeoutRef = useRef(null);

  const pool = useMemo(() => {
    const arr = [...placeholders];
    for (let j = arr.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      [arr[j], arr[k]] = [arr[k], arr[j]];
    }
    return arr;
  }, [model, active, placeholders]);

  useEffect(() => {
    const clear = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (!active || pool.length === 0) {
      clear();
      setI(0);
      return;
    }

    const scheduleNext = (currentIndex) => {
      const isFirst = currentIndex === 0;
      const basePersona = speedMs || 1000;

      // Base per-item delay (slow everything down hard)
      let base = basePersona * PER_ITEM_MULT;

      // Extra linger on the first placeholder
      if (isFirst) base *= FIRST_LINGER_MULT;

      // Apply jitter
      const jitter = base * JITTER_PCT;
      let delay = Math.round(base + (Math.random() * 2 - 1) * jitter);

      // Enforce a floor so nothing races
      delay = Math.max(MIN_DELAY_MS, delay);

      timeoutRef.current = setTimeout(() => {
        setI((prev) => {
          const next = (prev + 1) % pool.length;

          // If we’re about to wrap to the start, inject a big cycle pause
          if (next === 0) {
            const cyclePause = Math.max(
              MIN_DELAY_MS,
              Math.round((speedMs || 1000) * PER_ITEM_MULT * CYCLE_PAUSE_MULT)
            );
            timeoutRef.current = setTimeout(() => scheduleNext(0), cyclePause);
          } else {
            scheduleNext(next);
          }

          return next;
        });
      }, delay);
    };

    clear();
    setI(0);
    scheduleNext(0);

    return clear;
  }, [active, pool, speedMs]);

  return pool[i] ?? "";
}
